import { mktxDealersForSubmit } from './marketAxessDealers';
import {
  LIST_RFQ_NAME_REQUIRED,
  MAX_LIST_RFQ_BONDS,
  MAX_LIST_RFQ_NAME_LENGTH,
  MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT,
  MKTX_DEALER_LIST_MODES,
  mktxStrategyUsesDealers,
  ORDER_SIDES,
  PROTOCOL_TYPES,
  TRUMID_PRICE_TYPES,
  TRUMID_RFQ_INQUIRY_TYPES,
  MKTX_INQUIRY_TYPES,
  MKTX_DEFAULT_EXPIRY_TIME_UTC,
} from '../../Constants/AppConstants';
import { ROW_STATUS } from '../../Store/listRfqStore';
import { getCachedStrategyParamDefs, isDealerSelectionBlocked } from './optionsHelper';
import { deriveParamMeta, validateParamValue } from './strategyParamUtils';

/** Whether the list-level/per-row Trumid-only fields apply - see isTrumid in ListRfqTab. */
const isTrumidProtocol = (listRfqData = {}) => listRfqData.protocol === PROTOCOL_TYPES.TRUMID_RFQ;

/**
 * Whether dealer targeting (Dealer List Mode/Dealers, list-level) matters to this list at
 * all - the list-level generalisation of optionsHelper.isDealerRequired's SEEK/PROVIDE
 * exception from the single-bond ticket.
 *
 * There, strategyType is one field on one order, so "does dealer targeting apply" is a
 * single yes/no per order: SEEK/PROVIDE strategies never target dealers, so venue/
 * mktxDealerListMode drop out of requiredFields when strategyType is either. Here,
 * strategyType is per bond (row.strategyType) while Dealer List Mode/Dealers stay one
 * list-level control (NewOrderListService.requireSameListLevelValue) - a single row cannot
 * answer the question alone. The natural generalisation of "this order's strategy needs it"
 * to a whole list is "at least one bond's strategy needs it": if every bond is SEEK or
 * PROVIDE, nothing on the list would use dealer targeting even if it were set, so requiring
 * it would block sending a legitimate list for no wire-level reason.
 */
const dealerTargetingApplies = (rows = []) =>
  rows.some((row) => mktxStrategyUsesDealers(row.strategyType));

/**
 * >957 - the wire shape (paramId/paramName/dataType/value) OrderRequest.StrategyParameters
 * expects, built from one row's own values plus its strategy type's cached definitions
 * (optionsHelper.getCachedStrategyParamDefs - see that module's comment for why the cache
 * lives there rather than being threaded through as another buildListRfqPayload argument).
 *
 * Mirrors useOrderEntry's own non-edit-mode strategyParams build exactly (its `else` branch
 * next to buildStrategyParamsDelta): keep only params with a real value. A list bond is never
 * "editing an existing RFQ" the way that branch's sibling is - list creation has no edit mode
 * at all - so buildStrategyParamsDelta's delta-against-existing-values logic has no
 * equivalent here. Iterating `defs` rather than `Object.keys(values)` also does the job
 * useOrderEntry's separate filterStrategyValues helper does (dropping a stale value left over
 * from a strategy type the row no longer has) without needing that helper duplicated too - a
 * value for a paramName not in the current defs is simply never visited.
 */
const buildStrategyParamsForRow = (defs = [], values = {}) =>
  defs
    .filter((d) => {
      const paramName = d?.paramName;
      const paramValue = paramName ? values[paramName] : undefined;
      return paramName && paramValue !== undefined && paramValue !== null && paramValue !== '';
    })
    .map((d) => ({
      paramId: d.id,
      paramName: d.paramName,
      dataType: d.dataType,
      value: values[d.paramName],
    }));

/**
 * Turning what the List RFQ tab holds into what POST /order/list expects, plus the checks
 * that run before we send anything.
 *
 * Kept as plain functions with no React and no store access so they can be read and tested
 * on their own. The tab renders; this decides what goes on the wire.
 */

/** Minutes in the UI, seconds on the wire - identical to useOrderEntry's `toSeconds`. */
const toSeconds = (minutes) =>
  minutes === null || minutes === undefined || minutes === ''
    ? null
    : Number(minutes) * 60;

const parseMktxUtcMs = (value) => {
  if (!value) return NaN;
  const text = String(value).trim();
  const timestamp = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(text) ? text : `${text}Z`).getTime();
  return Number.isFinite(timestamp) ? timestamp : NaN;
};

// The single ticket refreshes expired defaults immediately before submit. A list can stay
// open overnight, so do the same per bond rather than sending yesterday's 168/126 values.
const currentMktxTimes = (row, now = new Date()) => {
  const nowMs = now.getTime();
  let expiryTime = row.expiryTime;
  if (Number.isFinite(parseMktxUtcMs(expiryTime)) && parseMktxUtcMs(expiryTime) <= nowMs) {
    const [timePart, msPart] = (MKTX_DEFAULT_EXPIRY_TIME_UTC || '20:57:00.000').split('.');
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    expiryTime = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
      hours, minutes, seconds, Number(msPart || '0'))).toISOString().slice(0, -1);
  }
  let effectiveTime = row.effectiveTime;
  if (Number.isFinite(parseMktxUtcMs(effectiveTime)) && parseMktxUtcMs(effectiveTime) <= nowMs) {
    const expiryMs = parseMktxUtcMs(expiryTime);
    effectiveTime = new Date(Number.isFinite(expiryMs) ? Math.min(nowMs, expiryMs) : nowMs)
      .toISOString().slice(0, -1);
  }
  return { effectiveTime: effectiveTime || null, expiryTime: expiryTime || null };
};

/**
 * Collapses the dealer multi-select into the single string the backend reads.
 *
 * "ALL" when the trader ticked ALL, or ticked every dealer on offer; otherwise the picked
 * dealers comma-separated and sorted.
 *
 * This repeats the logic that sits inline in useOrderEntry.handleBondOrderSubmit (the
 * `hasAllSelected` / `allDealersSelected` block). It is duplicated rather than shared
 * because pulling it out of useOrderEntry would mean editing the working single-bond
 * submit path, which is deliberately left untouched by this work. If that path is ever
 * refactored, it should call this instead - the two must stay in step, since the backend
 * maps "ALL" to no 453 group and anything else to one disclosed party per dealer
 * (QuoteRequestMapper.mapDealers).
 */
export const resolveVenueForSubmit = (venueSelection, availableDealers = []) => {
  const selected = (Array.isArray(venueSelection) ? venueSelection : [])
    .map((venue) => String(venue).trim())
    .filter(Boolean);

  if (selected.includes('ALL')) return 'ALL';

  const dealers = selected.filter((venue) => venue !== 'ALL').sort((a, b) => a.localeCompare(b));
  const offered = (availableDealers || []).map((venue) => String(venue).trim()).filter(Boolean);
  const everyDealerPicked =
    offered.length > 0 && offered.every((dealer) => dealers.includes(dealer));

  if (everyDealerPicked) return 'ALL';
  return dealers.join(',');
};

/** Rows the trader actually filled in - a blank trailing row is not an error. */
export const meaningfulRows = (rows = []) =>
  rows.filter(
    (row) =>
      (row.cusip || '').trim() ||
      (row.qty || '').toString().trim() ||
      (row.price || '').toString().trim()
  );

/**
 * Which rows carry an identifier that another row also carries.
 *
 * In plain terms: find the repeated bonds, and say which rows they are on.
 *
 * The single rule behind three separate behaviours, so they cannot disagree with each other:
 * the red border on the offending cells (ListRfqBondRow), the disabled Send button
 * (OrderEntryCore), and the submit-time error below. Comparison is on the trimmed,
 * upper-cased identifier - the same normalising buildListRfqPayload applies before sending
 * tag 48 - so "abc123xy9 " and "ABC123XY9" count as the same bond.
 *
 * Rows with an empty identifier are ignored: several blank rows are not duplicates of one
 * another. Every row holding a repeated identifier is returned, not just the later ones, so
 * the trader sees both ends of the clash rather than having to guess which row is the
 * "original".
 *
 * @param   {Array} rows bond rows from the store
 * @returns {Map<string, string>} rowId -> the normalised identifier it repeats
 */
export const findDuplicateCusips = (rows = []) => {
  const rowIdsByCusip = new Map();

  rows.forEach((row) => {
    const cusip = (row?.cusip || '').trim().toUpperCase();
    if (!cusip) return;
    if (!rowIdsByCusip.has(cusip)) rowIdsByCusip.set(cusip, []);
    rowIdsByCusip.get(cusip).push(row.rowId);
  });

  const duplicates = new Map();
  rowIdsByCusip.forEach((rowIds, cusip) => {
    if (rowIds.length > 1) rowIds.forEach((rowId) => duplicates.set(rowId, cusip));
  });

  return duplicates;
};

/**
 * Is any bond on the list entered twice?
 *
 * Returns a plain boolean on purpose: it is read through a zustand selector to disable the
 * Send button, and a selector returning a fresh Map or array on every call would compare
 * unequal each time and re-render the whole modal on any keystroke.
 */
export const hasDuplicateCusips = (rows = []) => findDuplicateCusips(rows).size > 0;

/**
 * Everything that must be true before we are willing to send.
 *
 * The first four checks mirror what OrdersCommandHandler.createOrderList and
 * QuoteRequestServiceTrumid.createListRfqDto enforce server-side. They are repeated here so
 * the trader gets told in the ticket instead of after a round trip - the backend keeps its
 * own copy because the API is reachable without this UI.
 *
 * HOW A PROBLEM IS REPORTED - the same way the Bond Order tab reports one.
 *
 * In plain terms: a bad field goes red, and nothing is written out in words.
 *
 * `fieldErrors` and `rowErrors` are both maps of `true` per offending field, never text.
 * That is deliberately the shape useOrderEntry.handleBondOrderSubmit builds for one bond
 * (its `newErrors` object, ~line 785): it marks `newErrors[field] = true` and hands the map
 * to the inputs, which pass it to MUI as `error` and get the danger-coloured border from
 * the shared `inputStyle`. No helperText, no row banner, no footer sentence.
 *
 * `rowErrors` is therefore keyed rowId -> { fieldKey: true }, and the field keys are exactly
 * the LIST_RFQ_COLUMNS keys, so ListRfqBondRow can look each cell up by its own key without
 * a translation table between the two files.
 *
 * WHY EVERY CHECK IS INDEPENDENT rather than the else-if chain this used to be: the single
 * ticket marks every missing field in one pass (`requiredFields.forEach`), so a trader fixes
 * the whole row at once. An else-if chain would surface one red cell, then another after the
 * next submit. The two `qty` checks are the one exception and stay chained - a quantity that
 * is missing cannot also be below the bond's minimum, and they light the same cell anyway.
 *
 * @returns {{fieldErrors: object, rowErrors: object, hasErrors: boolean, message: string|null}}
 *          `message` is set ONLY when there is nothing on screen to turn red; see the
 *          empty-list case below. Everything else reports itself by going red.
 */
export const validateListRfq = (listRfqData = {}, rows = [], dealerOptions = null) => {
  const fieldErrors = {};
  const rowErrors = {};
  const filled = meaningfulRows(rows);

  /** Marks one cell of one row as bad, creating that row's map on first use. */
  const markRow = (rowId, field) => {
    if (!rowErrors[rowId]) rowErrors[rowId] = {};
    rowErrors[rowId][field] = true;
  };

  const isTrumid = isTrumidProtocol(listRfqData);
  const mktxDealersPerBond = !isTrumid;

  if (!listRfqData.account) fieldErrors.account = true;
  if (!listRfqData.protocol) fieldErrors.protocol = true;

  // Optional unless LIST_RFQ_NAME_REQUIRED is on; never longer than the column that stores it.
  const listName = (listRfqData.listName || '').trim();
  if ((LIST_RFQ_NAME_REQUIRED && !listName) || listName.length > MAX_LIST_RFQ_NAME_LENGTH) {
    fieldErrors.listName = true;
  }

  if (isTrumid) {
    if (!listRfqData.rfqInquiryType) fieldErrors.rfqInquiryType = true;

    const deadline = Number(listRfqData.quoteSubmissionDeadline);
    if (!listRfqData.quoteSubmissionDeadline || !Number.isInteger(deadline) || deadline < 2 || deadline > 30) {
      fieldErrors.quoteSubmissionDeadline = true;
    }

    // Quote firm time is required on BIN and meaningless otherwise (Trumid RFQ spec p.46).
    if (listRfqData.rfqInquiryType === TRUMID_RFQ_INQUIRY_TYPES.BIN) {
      const ttl = Number(listRfqData.ttl);
      if (!listRfqData.ttl || !Number.isInteger(ttl) || ttl < 2 || ttl > 30) {
        fieldErrors.ttl = true;
      }
    }
  } else {
    // 5626 MktxPricingProcess - required (no null guard server-side, see
    // FixNewOrderMapper.mktxPricingProcess) and must agree across every bond, so it is one
    // list-level field rather than a per-row one - see the mktxPricingProcess field on
    // ListRfqTab's Pricing Process select.
    if (!listRfqData.mktxPricingProcess) fieldErrors.mktxPricingProcess = true;

    // Dealers picked with no mode chosen would be sent but silently ignored
    // (FixNewOrderList's own class doc: "without it, per-bond dealer inclusions are silently
    // ignored"), which is a worse outcome than either field alone being empty - block it
    // rather than let a trader believe they restricted dealers when they did not.
    const hasDealers = Array.isArray(listRfqData.mktxVenue) && listRfqData.mktxVenue.length > 0;
    if (dealerTargetingApplies(filled) && hasDealers && !listRfqData.mktxDealerListMode) {
      fieldErrors.mktxDealerListMode = true;
    }

    // Mirrors the single-bond ticket's own SEEK/PROVIDE exception (useOrderEntry.jsx,
    // requiredFields wiring, via optionsHelper.isDealerRequired) - see dealerTargetingApplies
    // above for why "at least one row" is the right generalisation to a list. When it does
    // apply: Dealer List Mode is required outright, and Dealers is required only once the
    // mode is Include Only (Include At Least targets everyone by default, so an empty list
    // there is a valid "no extra restriction", matching isDealerRequired's own
    // INCLUDE_ONLY-only check).
    if (dealerTargetingApplies(filled)) {
      if (!listRfqData.mktxDealerListMode) fieldErrors.mktxDealerListMode = true;
      if (hasDealers && dealerOptions !== null &&
          isDealerSelectionBlocked({
            venue: listRfqData.mktxVenue,
            options: dealerOptions,
            optionsLoaded: dealerOptions.length > 0,
            protocol: PROTOCOL_TYPES.MARKETAXESS,
            strategyType: filled.find((row) => mktxStrategyUsesDealers(row.strategyType))?.strategyType,
            mktxDealerListMode: listRfqData.mktxDealerListMode,
          })) fieldErrors.mktxVenue = true;
    }
  }

  // One bond is enough - the same floor the backend enforces
  // (OrdersCommandHandler.createOrderList: "a list RFQ needs at least one bond"). An earlier
  // version of this file demanded two, which was stricter than the server for no reason.
  //
  // This is the one case that still speaks in words, because an empty grid has no cell to
  // turn red. Send is disabled while the list is empty (OrderEntryCore.listHasNoBonds), so a
  // trader never reaches it - it is the backstop for callers that get here another way.
  if (filled.length < 1) {
    return {
      fieldErrors,
      rowErrors,
      hasErrors: true,
      message: 'A list RFQ needs at least one bond.',
    };
  }

  if (filled.length > MAX_LIST_RFQ_BONDS) {
    return {
      fieldErrors,
      rowErrors,
      hasErrors: true,
      message: `A list RFQ can carry at most ${MAX_LIST_RFQ_BONDS} bonds - this one has ${filled.length}. Remove ${filled.length - MAX_LIST_RFQ_BONDS}.`,
    };
  }

  // A bond may appear only once. Ours, not Trumid's - the venue accepts a repeated CUSIP,
  // but a trade confirmation on the Drop Copy session is matched by (list id + CUSIP) and a
  // duplicate would make that pair ambiguous. Same reasoning and same rule as
  // OrdersCommandHandler.createOrderList.
  // Both offending rows go red on their identifier - the cell that actually holds the clash.
  // The grid already reds these two the moment the second one is typed, from this same
  // function via ListRfqTab's duplicateRowIds, so submitting only confirms what is on screen.
  findDuplicateCusips(filled).forEach((_cusip, rowId) => {
    markRow(rowId, 'cusip');
  });

  filled.forEach((row) => {
    // An identifier that is blank, still being looked up, or rejected by the security master
    // is the same red cell either way. The REASON a looked-up bond was refused stays on the
    // hover of the row's own error icon (ListRfqBondRow.statusIcon, fed by row.message) -
    // that text is a lookup result, not a validation message, and it says something the
    // border cannot.
    if (!(row.cusip || '').trim() || row.status !== ROW_STATUS.VALID) markRow(row.rowId, 'cusip');

    if (!row.side) markRow(row.rowId, 'side');

    // Chained, unlike the rest: a missing quantity cannot also be under the bond's minimum.
    // Both light the same cell. The minimum comes from the security master on lookup, the
    // same rule the single ticket applies to one bond (useOrderEntry, its `minQty` guard).
    if (!row.qty || Number(row.qty) <= 0) markRow(row.rowId, 'qty');
    else if (row.minQty != null && Number(row.qty) < Number(row.minQty)) markRow(row.rowId, 'qty');
    if (!isTrumid && Number(row.qty) % 1000 !== 0) markRow(row.rowId, 'qty');

    if (row.price === '' || row.price === null || Number(row.price) <= 0) markRow(row.rowId, 'price');

    if (!row.priceType) markRow(row.rowId, 'priceType');

    if (isTrumid) {
      // Stipulations are "Conditionally required when PriceType=6" - spec p.50. Neither this
      // nor the next check has a MarketAxess equivalent: FixNewOrderList.Line has no
      // stipulationType or allowPartialExecution field at all. Dealer targeting DOES have a
      // MarketAxess equivalent, just not this same per-row field - see orderDealers below and
      // ListRfqTab's own list-level Dealers field.
      if (row.priceType === TRUMID_PRICE_TYPES.SPREAD && !row.stipulationType) {
        markRow(row.rowId, 'stipulationType');
      }

      if (!row.allowPartialExecution) markRow(row.rowId, 'allowPartialExecution');

      if (!Array.isArray(row.venue) || row.venue.length === 0) markRow(row.rowId, 'venue');
    } else {
      // >847 TargetStrategy - genuinely per bond, unlike mktxPricingProcess above.
      if (!row.strategyType) markRow(row.rowId, 'strategyType');
      if (mktxDealersPerBond && mktxStrategyUsesDealers(row.strategyType) &&
          listRfqData.mktxDealerListMode === MKTX_DEALER_LIST_MODES.INCLUDE_ONLY &&
          !(listRfqData.mktxVenue?.length > 0) &&
          (!Array.isArray(row.orderDealers) || row.orderDealers.length === 0)) {
        markRow(row.rowId, 'orderDealers');
      }
      if (mktxDealersPerBond && mktxStrategyUsesDealers(row.strategyType) &&
          Array.isArray(row.orderDealers) && row.orderDealers.length &&
          dealerOptions !== null && isDealerSelectionBlocked({
            venue: row.orderDealers,
            options: dealerOptions,
            optionsLoaded: dealerOptions.length > 0,
            protocol: PROTOCOL_TYPES.MARKETAXESS,
            strategyType: row.strategyType,
            mktxDealerListMode: listRfqData.mktxDealerListMode,
          })) markRow(row.rowId, 'orderDealers');

      // The single ticket validates every entered strategy parameter against its server
      // definition before sending. Apply the same rule to each list bond.
      const definitions = getCachedStrategyParamDefs(row.strategyType);
      if (definitions.some((def) => {
        const value = (row.strategyParameters || {})[def.paramName];
        return value !== null && value !== undefined && value !== '' &&
          !validateParamValue(deriveParamMeta(def), value);
      })) markRow(row.rowId, 'strategyParameters');

      // >168/>126 EffectiveTime/ExpireTime - required, mirroring the single-bond ticket's own
      // requiredFields list (useOrderEntry.jsx), the floor the parity rule sets. In practice
      // createEmptyRow already defaults both the moment a row is created, so a trader only
      // ever sees this fire if that default somehow failed to apply - the same "safety net,
      // not something normally hit" role the single ticket's own requirement plays.
      if (!row.effectiveTime) markRow(row.rowId, 'effectiveTime');
      if (!row.expiryTime) markRow(row.rowId, 'expiryTime');
      if (row.effectiveTime && row.expiryTime
          && new Date(row.effectiveTime) > new Date(row.expiryTime)) {
        markRow(row.rowId, 'effectiveTime');
        markRow(row.rowId, 'expiryTime');
      }
    }
  });

  const hasErrors =
    Object.keys(fieldErrors).length > 0 || Object.keys(rowErrors).length > 0;

  // No message: every problem above has a cell of its own and reports itself by going red,
  // which is what the Bond Order tab does for its required fields - it calls
  // setOrderFieldErrors and returns false without calling setModalError.
  return { fieldErrors, rowErrors, hasErrors, message: null };
};

/**
 * Builds the request body: one object per bond, shaped exactly like the single-bond
 * POST /order body so the backend's per-bond handling is identical.
 *
 * The list-level values that must agree across the whole list are copied onto every bond
 * rather than sent once - Trumid's rfqInquiryType/ttl/quoteSubmissionDeadline
 * (QuoteRequestServiceTrumid.createListRfqDto -> requireSameListLevelValue) and MarketAxess's
 * mktxPricingProcess (NewOrderListService, same shape). Each protocol's own fields are built
 * separately below - see isTrumid - rather than sending both venues' fields on every request,
 * which is what the backend's shared OrderRequest DTO would otherwise silently accept.
 *
 * @param {object}   args.listRfqData    shared settings from the store
 * @param {Array}    args.rows           bond rows from the store
 * @param {string}   args.settlementDate yyyy-mm-dd, fetched once for the whole list
 * @param {string[]} args.dealerOptions  every dealer the picker offered, for the ALL rule
 */
export const buildListRfqPayload = ({
  listRfqData = {},
  rows = [],
  settlementDate = null,
  dealerOptions = [],
}) => {
  const isTrumid = isTrumidProtocol(listRfqData);
  const isBin = isTrumid && listRfqData.rfqInquiryType === TRUMID_RFQ_INQUIRY_TYPES.BIN;
  const mktxDealersPerBond = !isTrumid;
  const mktxDealerTargetingApplies = !isTrumid &&
    dealerTargetingApplies(meaningfulRows(rows));

  // Message-level, identical on every bond. The backend compares these across bonds and
  // refuses a list whose bonds disagree, rather than reading them off the first one.
  const listLevel = {
    account: listRfqData.account,
    // Read from the form rather than hardcoded, now that the tab shows a Protocol field.
    // Falls back to Trumid RFQ because that is the only protocol createOrderList accepted
    // before MarketAxess joined it (2026-09-25).
    protocol: listRfqData.protocol || PROTOCOL_TYPES.TRUMID_RFQ,
    orderVersion: 1,
    comments: listRfqData.comments || null,              // not a FIX tag - order_mst only
    listName: (listRfqData.listName || '').trim() || null, // list_order_seq; MA list-level 5634

    ...(isTrumid
      ? {
          rfqInquiryType: listRfqData.rfqInquiryType,          // 28006
          ttl: isBin ? toSeconds(listRfqData.ttl) : null,      // 28019, BIN only
          quoteSubmissionDeadline: toSeconds(listRfqData.quoteSubmissionDeadline), // 28020
        }
      : {
          // 5626 MktxPricingProcess - list-level, required, see validateListRfq. quoteSubmis
          // sionDeadline stays null: the single-bond MarketAxess ticket deliberately never
          // sends it either (ActualOrderTab clears it on every MarketAxess submit), so the
          // list path matches that rather than inventing a value the single ticket does not
          // offer.
          mktxPricingProcess: listRfqData.mktxPricingProcess,
          rfqInquiryType: MKTX_INQUIRY_TYPES.BIN,
          quoteSubmissionDeadline: null,
          // 20002 - converted from the display string the select shows (MKTX_DEALER_LIST_
          // MODES) to the submit form the backend's IncludeDealers enum matches by name,
          // at the exact same point and with the exact same lookup useOrderEntry uses for the
          // single-bond ticket (its dataToSubmit.mktxDealerListMode assignment).
          //
          // Sent as null when no bond on the list would use it (every row SEEK/PROVIDE) -
          // mirrors useOrderEntry's own `trimmedData.mktxDealerListMode = null` for that same
          // case on the single-bond ticket. See dealerTargetingApplies above: this can only
          // clear it for an all-SEEK/PROVIDE list, never for a mixed one, since the value is
          // shared across every bond's payload (requireSameListLevelValue) and a TAKE bond
          // elsewhere on the same list still needs it.
          mktxDealerListMode:
             dealerTargetingApplies(meaningfulRows(rows)) && listRfqData.mktxDealerListMode
              ? MKTX_DEALER_LIST_MODE_DISPLAY_TO_SUBMIT[listRfqData.mktxDealerListMode] ||
                listRfqData.mktxDealerListMode
              : null,
        }),
  };

  return meaningfulRows(rows).map((row) => {
    const isSpread = row.priceType === TRUMID_PRICE_TYPES.SPREAD;
    return {
      ...listLevel,

      // Per line item - everything the spec nests inside NoRelatedSym <146> (Trumid, pp.47-51)
      // or the NoOrders <73> group (MarketAxess).
      cusip: (row.cusip || '').trim().toUpperCase(),     // 48
      side: row.side || ORDER_SIDES.BUY,                 // 54
      qty: Number(row.qty),                              // 38
      priceType: row.priceType,                          // 423 - same PRICE/SPREAD values both venues use

      ...(isTrumid
        ? {
            stipulationType: isSpread ? row.stipulationType : null, // 234, spread only
            allowPartialExecution: row.allowPartialExecution,       // 28007
            venue: resolveVenueForSubmit(row.venue, dealerOptions), // 453, this bond's dealers
          }
        : {
            // >847 TargetStrategy - per bond, like every field below down to expiryTime.
            strategyType: row.strategyType,
            // >957 - per bond, built from this row's own values and its strategy type's
             // cached definitions. The submit gate loads definitions for every populated
             // row's strategy before calling this builder, even when its dialog was never
             // opened. An explicitly cleared value is omitted.
            strategyParams: buildStrategyParamsForRow(
              getCachedStrategyParamDefs(row.strategyType),
              row.strategyParameters || {}
            ),
            // 20002/9690 - list-level (listRfqData.mktxVenue, not row.venue): every bond must
            // carry the same value (NewOrderListService.requireSameListLevelValue), so it is
            // resolved once here and copied onto every bond, the same way listLevel's own
            // fields are copied above rather than read per row.
            venue: mktxDealerTargetingApplies
              ? mktxDealersForSubmit(listRfqData.mktxVenue)
              : '',
            // >20130 - genuinely per bond (row.orderDealers, not listRfqData.mktxVenue), unlike
            // the list-level Dealers field just above. Field names here
            // (dealerCode/exclude/restrictedQty) match OrderRequest's own MktxOrderDealerRequest
            // record exactly. exclude/restrictedQty are always null: this picker only offers
            // inclusion on this TAKE bond (see ListRfqBondRow's orderDealers cell), and
            // >>20133's own spec text defines an absent tag as include, so leaving exclude null
            // is not a placeholder - it is the correct value for what this field currently does.
            mktxOrderDealers:
              mktxDealersPerBond && mktxStrategyUsesDealers(row.strategyType) &&
                Array.isArray(row.orderDealers) && row.orderDealers.length > 0
                ? row.orderDealers.map((code) => ({ dealerCode: code, exclude: null, restrictedQty: null }))
                : null,
            // >5214 - per bond (row.mktxTargetLevel), optional, no cross-bond agreement check
            // (NewOrderListService applies none - same as effectiveTime/expiryTime below).
            // "The list's only limit-level concept" per FixNewOrderList's own class doc, since
            // >44 Price is never mapped on either the single or the list path - see the `price`
            // field's own comment further down.
            mktxTargetLevel: row.mktxTargetLevel ?? null,
            // >168/>126 - per bond (row.effectiveTime/row.expiryTime, not listRfqData), unlike
            // Pricing Process/Dealers above - no cross-bond agreement check on either
            // (NewOrderListService applies none). Sent as-is: localToUtc already produced the
            // exact string shape OrderRequest.effectiveTime/expiryTime expect, the same shape
            // useOrderEntry.jsx sends untouched for the single-bond ticket.
            ...currentMktxTimes(row),
          }),

      // 64 SettlDate, the same value on every bond. The date fetched at submit wins over
      // whatever the tab showed, exactly as the single-bond path does - useOrderEntry
      // overwrites trimmedData.settlementDate with fetchBondCalcSettlementDate's answer
      // immediately before sending.
      settlementDate: settlementDate || listRfqData.settlementDate || null,

      // Not a FIX tag on either venue's list mapper - this is the order's own price on
      // order_mst.
      price: Number(row.price),
      bondInventoryId: row.bondInventoryId ?? null,
    };
  });
};

/** HTTP list envelope; keep the existing per-bond builder and venue mappings intact. */
export const buildListOrderRequest = (options) => {
  const bonds = buildListRfqPayload(options);
  const commonFields = [
    'account', 'protocol', 'listName', 'comments',
    'settlementDate', 'rfqInquiryType', 'orderVersion',
  ];
  return {
    ...Object.fromEntries(commonFields.map((key) => [key, bonds[0]?.[key]])),
    items: bonds.map((bond) => Object.fromEntries(
      Object.entries(bond).filter(([key]) => !commonFields.includes(key))
    )),
  };
};
